import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Brain, CheckCircle, AlertTriangle, Settings, TrendingUp } from 'lucide-react';
import { useBills } from '@/hooks/useBills';

interface DocumentClassification {
  id: string;
  documentType: 'utility_bill' | 'invoice' | 'receipt' | 'statement' | 'contract';
  confidence: number;
  vendor: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  processingRoute: string;
  metadata: Record<string, any>;
}

export const IntelligentDocumentClassification: React.FC = () => {
  const { bills } = useBills();
  const [classifications, setClassifications] = useState<DocumentClassification[]>([]);
  const [isClassifying, setIsClassifying] = useState(false);

  const classifyDocuments = async () => {
    setIsClassifying(true);
    
    // Simulate AI classification process
    const mockClassifications: DocumentClassification[] = bills?.slice(0, 10).map((bill, index) => ({
      id: bill.id,
      documentType: ['utility_bill', 'invoice', 'receipt'][index % 3] as any,
      confidence: 85 + Math.random() * 15,
      vendor: bill.supplier_name || 'Unknown Vendor',
      category: ['electricity', 'gas', 'water', 'internet'][index % 4],
      priority: ['high', 'medium', 'low'][index % 3] as any,
      processingRoute: `route_${index % 3 + 1}`,
      metadata: {
        pages: Math.floor(Math.random() * 5) + 1,
        language: 'en',
        quality: 'high'
      }
    })) || [];

    setTimeout(() => {
      setClassifications(mockClassifications);
      setIsClassifying(false);
    }, 2000);
  };

  const routeDocument = (classification: DocumentClassification) => {
    console.log(`Routing document ${classification.id} to ${classification.processingRoute}`);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'utility_bill': return <FileText className="w-4 h-4" />;
      case 'invoice': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Intelligent Document Classification</h2>
          <p className="text-muted-foreground">AI-powered document analysis and routing</p>
        </div>
        <Button onClick={classifyDocuments} disabled={isClassifying} className="gradient-primary">
          <Brain className="w-4 h-4 mr-2" />
          {isClassifying ? 'Classifying...' : 'Start Classification'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{classifications.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">High Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {classifications.filter(c => c.priority === 'high').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Avg Confidence</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {classifications.length > 0 
                ? Math.round(classifications.reduce((acc, c) => acc + c.confidence, 0) / classifications.length)
                : 0}%
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Auto-Routed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {classifications.filter(c => c.confidence > 90).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="classifications" className="w-full">
        <TabsList>
          <TabsTrigger value="classifications">Classifications</TabsTrigger>
          <TabsTrigger value="routes">Processing Routes</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="classifications" className="space-y-4">
          {isClassifying ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center space-y-4">
                  <Brain className="w-12 h-12 mx-auto text-primary animate-pulse" />
                  <p className="text-muted-foreground">AI is analyzing documents...</p>
                  <Progress value={33} className="w-1/2 mx-auto" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {classifications.map((classification) => (
                <Card key={classification.id} className="border-l-4 border-l-primary">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getTypeIcon(classification.documentType)}
                        <div>
                          <h3 className="font-semibold text-foreground">{classification.vendor}</h3>
                          <p className="text-sm text-muted-foreground capitalize">
                            {classification.documentType.replace('_', ' ')} • {classification.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getPriorityColor(classification.priority) as any}>
                          {classification.priority}
                        </Badge>
                        <Badge variant="outline">
                          {Math.round(classification.confidence)}% confidence
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Route: {classification.processingRoute}</span>
                        <span>Pages: {classification.metadata.pages}</span>
                        <span>Quality: {classification.metadata.quality}</span>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => routeDocument(classification)}
                        disabled={classification.confidence < 85}
                      >
                        {classification.confidence > 90 ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Auto-Route
                          </>
                        ) : (
                          <>
                            <Settings className="w-4 h-4 mr-2" />
                            Manual Review
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="routes" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Route 1: Standard Processing', 'Route 2: Priority Processing', 'Route 3: Manual Review'].map((route, index) => (
              <Card key={route}>
                <CardHeader>
                  <CardTitle className="text-lg">{route}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Documents</span>
                      <span>{Math.floor(Math.random() * 50) + 10}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Avg Processing Time</span>
                      <span>{Math.floor(Math.random() * 5) + 2}min</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Success Rate</span>
                      <span>{90 + Math.floor(Math.random() * 10)}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Classification Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>Utility Bills</span>
                  <div className="flex items-center gap-2">
                    <Progress value={85} className="w-24" />
                    <span className="text-sm">85%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Invoices</span>
                  <div className="flex items-center gap-2">
                    <Progress value={92} className="w-24" />
                    <span className="text-sm">92%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Receipts</span>
                  <div className="flex items-center gap-2">
                    <Progress value={78} className="w-24" />
                    <span className="text-sm">78%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};